import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle, IconExclamationMark, IconCircleCheck } from "@tabler/icons";
import { ENUM_FORM_DISPLAY } from "../components/pageList";
import { useFormRender } from "../hooks/useFormRender";
import { useServerApi } from "../hooks/userServerApi";
import {
  getUserDetailFormSchema,
  PROFILE_TAB_KEYS,
} from "./operation/userDetailFormSchema";
import { Page } from "../layout/page";
import PageListHeader from "../components/pageListHeader";
import { useTabNavigate } from "../hooks/useTabNavigate";
import {
  getMcaSubmittedNotificationMessage,
  getMcaSubmittedNotificationTitle,
  isMcaIntercepted,
} from "../components/mca/mcaApprovalLabels";

const SELF_PROFILE_SCALAR_FIELDS = [
  "aliasName",
  "cname",
  "gender",
  "birthday",
  "educationLevel",
  "mariage",
  "phones",
  "emails",
  "addresses",
  "contacts",
  "providerLogin",
];

const pickSelfProfileValues = (values = {}) => {
  const patch = {};
  for (const field of SELF_PROFILE_SCALAR_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(values, field)) {
      patch[field] = values[field];
    }
  }
  const methods = values.consultantInfo?.paymentMethods;
  if (methods) {
    patch.consultantInfo = {
      paymentMethods: {
        type: methods.type ?? "",
        bank: methods.bank ?? "",
        name: methods.name ?? "",
        accNumber: methods.accNumber ?? "",
      },
    };
  }
  return patch;
};

const notifyProfileSubmitted = (result) => {
  const requestId = result?.data?.mcaRequest?._id;
  const message = getMcaSubmittedNotificationMessage(result);
  showNotification({
    color: "orange",
    icon: <IconAlertCircle size={18} />,
    title: getMcaSubmittedNotificationTitle(),
    message: requestId ? (
      <>
        {message}{" "}
        <Text
          component={Link}
          to={`/mca/requests/${requestId}`}
          size="xs"
          color="blue"
        >
          View request
        </Text>
      </>
    ) : (
      message
    ),
  });
};

const MyProfile = () => {
  const auth = useAuthUser();
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [pageTitle, setPageTitle] = useState(t("My Profile"));
  const [saving, setSaving] = useState(false);

  const formSchema = getUserDetailFormSchema(null, null, {
    title: "My Profile",
    displayMode: ENUM_FORM_DISPLAY.INSIDE,
    showSaveBar: false,
    tabKeys: PROFILE_TAB_KEYS,
    includeActionList: false,
    includeOverriding: false,
    readOnly: false,
    selfService: true,
    showProfileSummary: false,
    showSfcLicencesInCpd: false,
    consultantViewFiles: true,
    hideInternalFiles: true,
  });

  const [renderForm, formAction, formStatus] = useFormRender(
    formSchema,
    null,
    null,
    (values) => {
      if (values?.name) {
        setPageTitle(`${t("My Profile")} > ${values.name}`);
      }
    }
  );

  useEffect(() => {
    formAction.open({ mode: "edit", id: auth().id });
  }, []);

  const handleSave = async () => {
    if (formStatus.isMcaLocked) {
      showNotification({
        color: "orange",
        icon: <IconAlertCircle size={18} />,
        title: t("Changes blocked"),
        message: t(
          "This record has a pending MCA approval. Use the MCA request to revise or cancel it."
        ),
      });
      return;
    }

    setSaving(true);
    try {
      const result = await api.updateOwnProfile({
        values: pickSelfProfileValues(formAction.getValues()),
      });

      if (!result?.success) {
        throw new Error(api.getErrorMsg(result?.error) || result?.message);
      }

      if (isMcaIntercepted(result)) {
        notifyProfileSubmitted(result);
      } else {
        showNotification({
          title: t("Save Success"),
          message: t("Profile updated"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
        });
      }
      await formAction.reload();
    } catch (error) {
      showNotification({
        title: t("Save Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={pageTitle}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: formStatus.isMcaLocked,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {renderForm()}
    </Page>
  );
};

export default MyProfile;
